import { faPen } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, BoxProps, Group, Input, MantineSize, TextInput, TextInputProps, useMantineTheme } from '@mantine/core';
import { InputSizes } from '@vision/theme';
import clsx from 'clsx';
import React, { useRef, useState } from 'react';
import { If } from '../If';
import classes from './InlineEdit.module.css';

const DRAG_THRESHOLD = 5;

interface InlineEditProps extends TextInputProps {
  readViewWrapperProps?: BoxProps;
  startWithEditViewOpen?: boolean;
  iconClassName?: string;
}

export function InlineEdit({
  children,
  description,
  error,
  label,
  readViewWrapperProps,
  size,
  startWithEditViewOpen,
  withAsterisk,
  onBlur,
  disabled,
  iconClassName,
  ...props
}: React.PropsWithChildren<InlineEditProps>) {
  const startX = useRef(0);
  const startY = useRef(0);
  const theme = useMantineTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isEditingState, setEditingState] = useState((!!error || startWithEditViewOpen) ?? false);

  const mouseHasMovedAfterMouseDown = (event: { clientX: number; clientY: number }) => {
    return (
      Math.abs(startX.current - event.clientX) >= DRAG_THRESHOLD ||
      Math.abs(startY.current - event.clientY) >= DRAG_THRESHOLD
    );
  };

  const handleReadViewClick = (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
    if (disabled) {
      return;
    }
    const element = event.target as HTMLElement;
    /** If a link is clicked in the read view, default action should be taken */
    if (element.tagName.toLowerCase() !== 'a' && !mouseHasMovedAfterMouseDown(event)) {
      event.preventDefault();
      setEditingState(true);
      setTimeout(() => {
        inputRef.current.focus();
      }, 0);
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement, Element>) => {
    if (onBlur) {
      onBlur(event);
    }
    if (!error && inputRef.current.value.length > 0) {
      setEditingState(false);
    }
  };

  if (isEditingState) {
    return (
      <div className={classes.editView}>
        <TextInput
          ref={inputRef}
          onBlur={handleBlur}
          description={description}
          error={error}
          label={label}
          size={size}
          withAsterisk={withAsterisk}
          {...props}
        />
      </div>
    );
  }

  return (
    <Box {...readViewWrapperProps} className={clsx(classes.readView, readViewWrapperProps?.className)}>
      <Input.Wrapper>
        {!!label && <Input.Label required={withAsterisk}>{label}</Input.Label>}
        {!!description && <Input.Description>{description}</Input.Description>}
        <Group
          h={InputSizes[(size as MantineSize) || 'sm']}
          onClick={handleReadViewClick}
          onMouseDown={(e) => {
            startX.current = e.clientX;
            startY.current = e.clientY;
          }}
          gap={10}
          wrap="nowrap"
        >
          {children}
          <If value={!disabled}>
            <FontAwesomeIcon
              icon={faPen}
              color={theme.colors.gray[1]}
              className={clsx(classes.penIcon, iconClassName)}
            />
          </If>
        </Group>
        {!!error && isEditingState && <Input.Error>{error}</Input.Error>}
      </Input.Wrapper>
    </Box>
  );
}
